<template>
    <transition name="slide">
        <div
            v-if="visible"
            :class="[
                'side-panel',
                size,
                { 'align-left': alignLeft, 'height-locked': noScroll },
            ]"
            ref="panel"
            :style="panelStyle"
        >
            <div class="panel-head gap-3">
                <slot name="top-left"></slot>
                <h2 v-if="title" class="panel-title">{{ title }}</h2>
                <button
                    v-if="closeVisible"
                    type="button"
                    class="close-btn icon-btn icon-btn-white"
                    @click="closePanelFromButton"
                >
                    <i class="fi fi-x"></i>
                </button>
            </div>

            <div class="panel-contents-wrapper">
                <div>
                    <slot name="header"></slot>
                </div>

                <div
                    class="panel-body"
                    :style="bodyStyle"
                    :class="{ 'height-locked': noScroll }"
                >
                    <slot></slot>
                </div>

                <div>
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </transition>

    <!-- Overlay div to close the panel when clicking outside -->
    <div v-if="visible" class="overlay" @click="closePanelFromOverlay"></div>

    <Modal :visible="isModalVisible" @close="closeModal">
        <div class="d-flex flex-column align-items-center">
            <i
                class="fi fi-lg fi-alert-circle mb-2"
                style="font-size: 34px; line-height: 34px"
            ></i>
            <h2 class="my-1">Exit?</h2>
            <p class="my-2" style="color: var(--neutral-800)">
                Are you sure you want to exit? All progress will be lost.
            </p>
            <div>
                <button
                    type="button"
                    class="btn btn-desktop-lg btn-white me-2"
                    @click="closeModal"
                >
                    No
                </button>
                <button
                    type="button"
                    class="btn btn-desktop-lg btn-primary"
                    @click="closePanel"
                >
                    Exit
                </button>
            </div>
        </div>
    </Modal>
</template>

<script>
import Modal from './Modal.vue';

export default {
    name: 'SidePanel',
    components: {
        Modal,
    },
    props: {
        // toggles panel visibility
        visible: {
            type: Boolean,
            required: true,
        },
        // panel width: sm-200 px, md-400 px, lg-800 px, full-100%
        size: {
            type: String,
            default: 'panel-md',
            validator(value) {
                return [
                    'panel-sm',
                    'panel-md',
                    'panel-lg',
                    'panel-xl',
                    'panel-full',
                ].includes(value);
            },
        },
        // The displayed title on the panel
        title: {
            type: String,
            default: null,
        },
        // Exit button displayed if true
        closeVisible: {
            type: Boolean,
            default: true,
        },
        // If true, aligns panel on left side of page
        alignLeft: {
            type: Boolean,
            default: false,
        },
        // If true, lock height from overflowing
        noScroll: {
            type: Boolean,
            default: false,
        },
        bodyStyle: {
            type: String,
            default: '',
        },
        panelStyle: {
            type: [String, Object],
            default: '',
        },
        confirmClose: {
            type: [Boolean, Function],
            default: false,
        },
    },
    data() {
        return {
            isModalVisible: false,
            closeSource: null, // 'button' or 'overlay'
        };
    },
    watch: {
        visible(newValue) {
            if (newValue) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        },
    },
    emits: ['close'],
    methods: {
        closePanelFromButton() {
            this.closeSource = 'button';
            this.closePanel();
        },
        closePanelFromOverlay() {
            this.closeSource = 'overlay';
            this.closePanel();
        },
        closePanel() {
            // Support both boolean and function for confirmClose
            const shouldConfirm =
                typeof this.confirmClose === 'function'
                    ? this.confirmClose()
                    : this.confirmClose;

            if (shouldConfirm && !this.isModalVisible) {
                this.isModalVisible = true;
            } else {
                // Store the source before closing modal
                const source = this.closeSource;
                this.isModalVisible = false;
                this.$emit('close', source);
                this.closeSource = null;
            }
        },
        closeModal() {
            this.isModalVisible = false;
            this.closeSource = null;
        },
    },
};
</script>

<style scoped>
.side-panel {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 1.25rem;
    right: 1.25rem;
    bottom: 1.25rem;
    width: 100%;
    border-radius: var(--radius-lg);
    max-height: 100%;
    background-color: white;
    z-index: var(--z-index-side-panel);
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
}

.panel-head {
    display: flex;
    align-items: flex-end;
    position: relative;
    background-color: var(--neutral-500);
    padding: var(--space-5) var(--space-5);
    padding-right: 3.3rem;
    min-height: 5rem;
    height: auto;
    max-height: 7rem;
    overflow-y: hidden;
}

.panel-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    align-self: flex-end;
}

.panel-body {
    overflow-x: hidden;
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
    min-height: 0;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
}

.panel-contents-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: var(--space-6);
    overflow: hidden;
    min-height: 0;
}

.height-locked {
    overflow-y: hidden;
}

.side-panel.align-left {
    left: 1.25rem;
    right: auto;
}

.side-panel.panel-sm {
    max-width: min(300px, calc(100% - 2.5rem));
}

.side-panel.panel-md {
    max-width: min(500px, calc(100% - 2.5rem));
}

.side-panel.panel-lg {
    max-width: min(800px, calc(100% - 2.5rem));
}

.side-panel.panel-xl {
    max-width: min(1000px, calc(100% - 2.5rem));
}

.side-panel.panel-full {
    width: calc(100% - 2.5rem);
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
    /* Panel off the screen (on the right) */
    transform: translateX(100%);
}

.slide-enter-from.align-left,
.slide-leave-to.align-left {
    /* Panel off the screen (on the left) */
    transform: translateX(-100%);
}

/* Overlay to cover the rest of the page */
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* semi-transparent overlay */
    background-color: rgba(64, 89, 81, 0.25);
    /* one level below the panel */
    z-index: var(--z-index-overlay);
}

/* Scrollbar behavior */
.panel-body::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.panel-body:hover::-webkit-scrollbar {
    opacity: 1;
}

.panel-body::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.panel-body::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.panel-body::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

@media only screen and (max-width: 767.98px) {
    .side-panel {
        top: var(--space-2);
        right: var(--space-2);
        bottom: var(--space-2);
    }

    .side-panel.align-left {
        left: var(--space-2);
    }

    .side-panel.panel-sm {
        max-width: min(300px, calc(100% - 2 * var(--space-2)));
    }

    .side-panel.panel-md {
        max-width: min(500px, calc(100% - 2 * var(--space-2)));
    }

    .side-panel.panel-lg {
        max-width: min(800px, calc(100% - 2 * var(--space-2)));
    }

    .side-panel.panel-xl {
        max-width: min(1000px, calc(100% - 2 * var(--space-2)));
    }

    .side-panel.panel-full {
        width: calc(100% - 2 * var(--space-2));
    }

    .panel-contents-wrapper {
        padding: var(--space-4);
    }

    .panel-head {
        padding: var(--space-4);
        padding-top: var(--space-3);
        padding-right: 4rem;
        min-height: 3.75rem;
    }

    .close-btn {
        top: var(--space-3);
        right: var(--space-3);
    }
}
</style>
